import { useState } from 'react';
import { Link } from 'react-router';
import { CountrySelect } from '../../components/CountrySelect';
import { Badge, Empty, ErrorMsg, KV, Section, Sq, StatusMark, StudioLogo, Time, Unknown } from '../../components/ui';
import { api, ApiError, errorText } from '../../lib/api';
import { FIELD_LABEL, fieldValue, INTEREST_LABEL, jobType, PRICING_LABEL } from '../../lib/format';
import { useSession } from '../../lib/session';
import type { Evidence, Fact, Job, StudioDetail } from '../../lib/types';
import { MissingInfoPanel } from './MissingInfo';

function Locked({ on }: { on: boolean }) {
  if (!on) return null;
  return (
    <span className="locked" title="Edited by you — collection will only propose changes to this field">
      <Sq kind="outline" /> edited by you — collection will only propose
    </span>
  );
}

const ID_LABEL: Record<string, string> = {
  bg_eik: 'Bulgarian UIC (ЕИК)',
  vat: 'EU VAT number',
  fr_siren: 'French SIREN',
  uk_company: 'UK company number',
};
const BG_REGISTER = 'https://portal.registryagency.bg/CR/';

function CompanyIdentity({ studio }: { studio: StudioDetail }) {
  const ids = Object.entries(studio.company_ids);
  const rec = studio.company_record;
  if (!ids.length && !rec && !studio.logo) {
    return (
      <Empty>
        No logo or legal identifier found on the website yet. They are read from the homepage, footer and contact pages on each collection
        (Refresh).
      </Empty>
    );
  }
  return (
    <div className="stack-s">
      {studio.logo && (
        <p className="small">
          <StudioLogo src={studio.logo_url} /> Logo: {studio.logo.how}
          {studio.logo.url && (
            <>
              {' '}
              ·{' '}
              <a href={studio.logo.url} target="_blank" rel="noreferrer noopener">
                original
              </a>
            </>
          )}
        </p>
      )}
      {ids.length > 0 && (
        <KV
          rows={ids.map(([scheme, v]) => [
            ID_LABEL[scheme] ?? scheme,
            <>
              <span className="num">{v.value}</span>
              {v.url && (
                <span className="muted small">
                  {' '}
                  · stated on{' '}
                  <a href={v.url} target="_blank" rel="noreferrer noopener">
                    {v.url}
                  </a>
                </span>
              )}
              {scheme === 'bg_eik' && (
                <div className="small muted">
                  No official Bulgarian source publishes a company’s headcount, so it is not looked up automatically. Check the owners, seat and
                  activity in the{' '}
                  <a href={BG_REGISTER} target="_blank" rel="noreferrer noopener">
                    Commercial Register
                  </a>{' '}
                  with this ЕИК.
                </div>
              )}
            </>,
          ])}
        />
      )}
      {rec && (
        <>
          <h3 className="sub">Official record</h3>
          <KV
            rows={[
              ['Legal name', rec.legal_name ?? <Unknown />],
              ['Employees', rec.headcount ? `${rec.headcount.label}${rec.headcount.year ? ` (${rec.headcount.year})` : ''}` : <Unknown>not stated by the register</Unknown>],
              ['Created', rec.created_on ?? <Unknown />],
              ['Status', rec.active === null ? <Unknown /> : rec.active ? 'active' : 'closed'],
              ['Registered address', rec.address ?? <Unknown />],
            ]}
          />
          <p className="small muted">
            {rec.registry_label}, read <Time iso={rec.fetched_at} /> ·{' '}
            <a href={rec.page_url} target="_blank" rel="noreferrer noopener">
              open the company page
            </a>{' '}
            · {rec.licence}. The band counts salaried employees of the whole company; freelancers are not in it.
          </p>
        </>
      )}
    </div>
  );
}

type Draft = {
  name: string;
  website_url: string;
  country: string;
  city: string;
  team_size_band: string;
  team_size_source: string;
  team_size_confidence: string;
  summary: string;
  services: string[];
  client_types: string[];
  markets_served: string;
  pricing_evidence: string;
  last_activity_at: string;
  last_activity_note: string;
};

function draftOf(s: StudioDetail): Draft {
  return {
    name: s.name,
    website_url: s.website_url ?? '',
    country: s.country ?? '',
    city: s.city ?? '',
    team_size_band: s.team_size_band ?? '',
    team_size_source: s.team_size_source ?? '',
    team_size_confidence: s.team_size_confidence ?? '',
    summary: s.summary ?? '',
    services: s.services,
    client_types: s.client_types,
    markets_served: s.markets_served.join(', '),
    pricing_evidence: s.pricing_evidence,
    last_activity_at: s.last_activity_at ? s.last_activity_at.slice(0, 10) : '',
    last_activity_note: s.last_activity_note ?? '',
  };
}

function changesOf(s: StudioDetail, d: Draft): Record<string, unknown> {
  const before = draftOf(s);
  const out: Record<string, unknown> = {};
  const str = (k: keyof Draft) => {
    if (before[k] !== d[k]) out[k] = (d[k] as string).trim() || null;
  };
  (['name', 'website_url', 'country', 'city', 'team_size_band', 'team_size_source', 'team_size_confidence', 'summary', 'last_activity_note'] as const).forEach(str);
  if (before.pricing_evidence !== d.pricing_evidence) out.pricing_evidence = d.pricing_evidence;
  if (before.last_activity_at !== d.last_activity_at) out.last_activity_at = d.last_activity_at || null;
  for (const k of ['services', 'client_types'] as const) {
    if (before[k].join('|') !== d[k].join('|')) out[k] = d[k];
  }
  if (before.markets_served !== d.markets_served)
    out.markets_served = d.markets_served
      .split(/[,\s]+/)
      .map((x) => x.trim())
      .filter(Boolean);
  return out;
}

function EditForm({ studio, onSaved, onCancel, onConflict }: { studio: StudioDetail; onSaved: (msg: string) => void; onCancel: () => void; onConflict: (msg: string) => void }) {
  const { settings } = useSession();
  const v = settings?.values;
  const [d, setD] = useState<Draft>(() => draftOf(studio));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setD({ ...d, [k]: e.target.value });
  const svcOptions = Array.from(new Set([...(v?.service_categories ?? []), ...studio.services]));
  const clientOptions = Array.from(new Set([...(v?.client_categories ?? []), ...studio.client_types]));
  const bands = Array.from(new Set([...(v?.team_size_bands ?? []), ...(studio.team_size_band ? [studio.team_size_band] : [])]));

  async function save(e: React.FormEvent) {
    e.preventDefault();
    const changes = changesOf(studio, d);
    if (!Object.keys(changes).length) {
      onCancel();
      return;
    }
    setBusy(true);
    setError('');
    try {
      const r = await api<{ changed: string[] }>(`/api/studios/${studio.id}`, { method: 'PATCH', body: { version: studio.version, changes } });
      onSaved(r.changed.length ? `Saved: ${r.changed.map((f) => FIELD_LABEL[f] ?? f).join(', ')}.` : 'Nothing changed.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && /changed since you loaded/.test(err.detail)) {
        onConflict(`Someone else edited this studio while you were editing (${err.detail}). The latest version is loaded below; your changes were not saved.`);
      } else setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const toggle = (k: 'services' | 'client_types', o: string, on: boolean) => setD({ ...d, [k]: on ? [...d[k], o] : d[k].filter((x) => x !== o) });

  return (
    <form onSubmit={save} className="form-grid" aria-label="Edit studio">
      <label className="field">
        <span>Name</span>
        <input value={d.name} onChange={set('name')} required />
      </label>
      <label className="field">
        <span>Website</span>
        <input value={d.website_url} onChange={set('website_url')} inputMode="url" />
      </label>
      <label className="field">
        <span>HQ country</span>
        <CountrySelect value={d.country} onChange={(country) => setD({ ...d, country })} />
      </label>
      <label className="field">
        <span>HQ city</span>
        <input value={d.city} onChange={set('city')} />
      </label>
      <label className="field">
        <span>Team size</span>
        <select value={d.team_size_band} onChange={set('team_size_band')}>
          <option value="">unknown</option>
          {bands.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Team size confidence</span>
        <select value={d.team_size_confidence} onChange={set('team_size_confidence')}>
          <option value="">—</option>
          <option value="low">low</option>
          <option value="medium">medium</option>
          <option value="high">high</option>
        </select>
      </label>
      <label className="field span2">
        <span>Team size source</span>
        <input value={d.team_size_source} onChange={set('team_size_source')} placeholder="e.g. LinkedIn page, team page" />
      </label>
      <label className="field span2">
        <span>Summary</span>
        <textarea rows={3} value={d.summary} onChange={set('summary')} />
      </label>
      <fieldset className="field span2">
        <legend>Services</legend>
        <div className="checklist">
          {svcOptions.map((o) => (
            <label key={o} className="check">
              <input type="checkbox" checked={d.services.includes(o)} onChange={(e) => toggle('services', o, e.target.checked)} /> {o}
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="field span2">
        <legend>Client types</legend>
        <div className="checklist">
          {clientOptions.map((o) => (
            <label key={o} className="check">
              <input type="checkbox" checked={d.client_types.includes(o)} onChange={(e) => toggle('client_types', o, e.target.checked)} /> {o}
            </label>
          ))}
        </div>
      </fieldset>
      <label className="field">
        <span>Markets served (ISO codes)</span>
        <input value={d.markets_served} onChange={set('markets_served')} placeholder="BG, RO" />
      </label>
      <label className="field">
        <span>Pricing evidence</span>
        <select value={d.pricing_evidence} onChange={set('pricing_evidence')}>
          {Object.entries(PRICING_LABEL).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Last activity (date)</span>
        <input type="date" value={d.last_activity_at} onChange={set('last_activity_at')} />
      </label>
      <label className="field">
        <span>Last activity note</span>
        <input value={d.last_activity_note} onChange={set('last_activity_note')} />
      </label>
      <div className="span2">
        <ErrorMsg>{error}</ErrorMsg>
        <p className="muted small">Fields you change become “edited by you”: later website collection will only propose new values for them.</p>
        <div className="row gap">
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
          <button type="button" className="btn ghost" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}

function ProposedFacts({ facts, evidence, onReviewed }: { facts: Fact[]; evidence: Evidence | null; onReviewed: (msg: string) => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const sources = new Map((evidence?.sources ?? []).map((s) => [s.id, s]));

  async function review(f: Fact, decision: 'accept' | 'reject') {
    setBusy(f.id);
    setError('');
    try {
      await api(`/api/facts/${f.id}/review`, { method: 'POST', body: { decision } });
      onReviewed(`${FIELD_LABEL[f.field] ?? f.field}: proposal ${decision === 'accept' ? 'accepted' : 'rejected'}.`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  }

  if (!facts.length) return <Empty>No proposed facts. Website collection proposes values here when it finds something that differs from what you entered.</Empty>;
  return (
    <>
      <ErrorMsg>{error}</ErrorMsg>
      <ul className="facts">
        {facts.map((f) => {
          const src = f.source_id ? sources.get(f.source_id) : undefined;
          return (
            <li key={f.id} className="fact">
              <div className="fact-head">
                <strong>{FIELD_LABEL[f.field] ?? f.field}</strong>
                <span className="muted small">
                  {' '}
                  · {f.method.replace('_', ' ')} {f.confidence && `· confidence ${f.confidence}`} · <Time iso={f.observed_at} />
                </span>
              </div>
              <p className="fact-value">{fieldValue(f.value)}</p>
              {f.excerpt && <blockquote className="excerpt">{f.excerpt}</blockquote>}
              <p className="small muted">
                Source:{' '}
                {src?.url ? (
                  <a href={src.url} target="_blank" rel="noreferrer noopener">
                    {src.title || src.url}
                  </a>
                ) : (
                  src?.title ?? 'not recorded'
                )}
              </p>
              <div className="row gap">
                <button type="button" className="btn small primary" disabled={busy === f.id} onClick={() => void review(f, 'accept')}>
                  Accept
                </button>
                <button type="button" className="btn small" disabled={busy === f.id} onClick={() => void review(f, 'reject')}>
                  Reject
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function SocialForm({ studioId, onAdded }: { studioId: string; onAdded: (msg: string) => void }) {
  const { settings } = useSession();
  const [url, setUrl] = useState('');
  const [platform, setPlatform] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await api<{ outcome: string }>(`/api/studios/${studioId}/socials`, { method: 'POST', body: { url, platform: platform || null } });
      setUrl('');
      setPlatform('');
      onAdded(r.outcome === 'added' ? 'Social profile added.' : `Social profile: ${r.outcome.replace(/_/g, ' ')}.`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={submit} className="inline-form" aria-label="Add social profile">
      <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://instagram.com/…" required aria-label="Profile URL" inputMode="url" />
      <select value={platform} onChange={(e) => setPlatform(e.target.value)} aria-label="Platform">
        <option value="">detect platform</option>
        {(settings?.known_channels ?? []).filter((c) => c !== 'website').map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <button type="submit" className="btn small" disabled={busy}>
        Add
      </button>
      <ErrorMsg>{error}</ErrorMsg>
    </form>
  );
}

function TagEditor({ studio, onChanged }: { studio: StudioDetail; onChanged: (msg: string) => void }) {
  const [tag, setTag] = useState('');
  const [error, setError] = useState('');
  async function change(body: { add_tags?: string[]; remove_tags?: string[] }) {
    setError('');
    try {
      await api('/api/studios/bulk', { method: 'POST', body: { ids: [studio.id], ...body } });
      setTag('');
      onChanged('Tags updated.');
    } catch (e) {
      setError(errorText(e));
    }
  }
  return (
    <div>
      <ul className="chips">
        {studio.tags.length === 0 && <li className="muted small">No tags.</li>}
        {studio.tags.map((t) => (
          <li key={t} className="chip">
            {t}
            <button type="button" className="link small" aria-label={`Remove tag ${t}`} onClick={() => void change({ remove_tags: [t] })}>
              ×
            </button>
          </li>
        ))}
      </ul>
      <form
        className="inline-form"
        aria-label="Add tag"
        onSubmit={(e) => {
          e.preventDefault();
          if (tag.trim()) void change({ add_tags: [tag.trim()] });
        }}
      >
        <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="new tag" aria-label="New tag" />
        <button type="submit" className="btn small" disabled={!tag.trim()}>
          Add tag
        </button>
      </form>
      <ErrorMsg>{error}</ErrorMsg>
    </div>
  );
}

const ACCESS: Record<string, string> = {
  not_checked: 'not checked',
  no_adapter: 'no adapter — not collected',
  ok: 'reachable',
  blocked: 'blocked',
  not_permitted: 'not permitted (robots.txt) — not collected',
};
export const accessLabel = (s: string) => ACCESS[s] ?? s;

const CHANNEL: Record<string, string> = {
  scheduled: 'collected on schedule',
  no_adapter: 'no adapter yet, not collected',
  not_permitted: 'not permitted without an authorised API, not collected',
  demo_fictional: 'demo studio: fictional site, not collected',
};
export const channelLabel = (s: string | undefined) => CHANNEL[s ?? ''] ?? 'not collected';

export function OverviewTab({
  studio,
  evidence,
  notify,
  reload,
  jobs = null,
  checking = false,
  onCheck = null,
}: {
  studio: StudioDetail;
  evidence: Evidence | null;
  notify: (msg: string, tone?: 'ok' | 'warn') => void;
  reload: () => void;
  jobs?: Job[] | null;
  checking?: boolean;
  onCheck?: (() => void) | null;
}) {
  const [editing, setEditing] = useState(false);
  const locked = new Set(studio.locked_fields);
  const proposed = (evidence?.facts ?? []).filter((f) => f.review_state === 'proposed');
  const row = (field: string, value: React.ReactNode): [React.ReactNode, React.ReactNode] => [
    FIELD_LABEL[field] ?? field,
    <>
      {value} <Locked on={locked.has(field)} />
    </>,
  ];
  const done = (msg: string) => {
    notify(msg);
    reload();
  };

  return (
    <div className="studio-grid">
      <div className="studio-main">
        <Section
          title="Profile"
          aside={
            !editing && (
              <button type="button" className="btn small" onClick={() => setEditing(true)}>
                Edit
              </button>
            )
          }
        >
          {editing ? (
            <EditForm
              studio={studio}
              onCancel={() => setEditing(false)}
              onSaved={(m) => {
                setEditing(false);
                done(m);
              }}
              onConflict={(m) => {
                setEditing(false);
                notify(m, 'warn');
                reload();
              }}
            />
          ) : (
            <KV
              rows={[
                row('name', studio.name),
                row(
                  'website_url',
                  studio.website_url ? (
                    <a href={studio.website_url} target="_blank" rel="noreferrer noopener">
                      {studio.website_url}
                    </a>
                  ) : (
                    <Unknown>no website</Unknown>
                  ),
                ),
                row('city', studio.city ?? <Unknown />),
                row('country', studio.country ?? <Unknown />),
                row(
                  'team_size_band',
                  studio.team_size_band ? (
                    <>
                      {studio.team_size_band}
                      {studio.team_size_confidence && <span className="muted"> · confidence {studio.team_size_confidence}</span>}
                      {studio.team_size_source && <span className="muted"> · {studio.team_size_source}</span>}
                    </>
                  ) : (
                    <Unknown />
                  ),
                ),
                row('services', studio.services.length ? studio.services.join(', ') : <Unknown />),
                row('client_types', studio.client_types.length ? studio.client_types.join(', ') : <Unknown />),
                row('markets_served', studio.markets_served.length ? studio.markets_served.join(', ') : <Unknown />),
                row('summary', studio.summary ?? <Unknown>no summary</Unknown>),
                row('pricing_evidence', studio.pricing_evidence === 'not_checked' ? <Unknown>not checked</Unknown> : PRICING_LABEL[studio.pricing_evidence]),
                row(
                  'last_activity_at',
                  studio.last_activity_at ? (
                    <>
                      <Time iso={studio.last_activity_at} />
                      {studio.last_activity_note && <span className="muted"> · {studio.last_activity_note}</span>}
                    </>
                  ) : (
                    <Unknown>not measured</Unknown>
                  ),
                ),
                ['Last collected', studio.last_collected_at ? <Time iso={studio.last_collected_at} /> : <Unknown>never collected</Unknown>],
                ['Interest', INTEREST_LABEL[studio.interest]],
              ]}
            />
          )}
          {studio.missing.length > 0 && (
            <p className="small missing">
              <strong>Missing:</strong> {studio.missing.join(', ')}
            </p>
          )}
        </Section>

        <MissingInfoPanel studio={studio} jobs={jobs} checking={checking} onCheck={onCheck} notify={notify} reload={reload} />

        <Section title="Company identity">
          <CompanyIdentity studio={studio} />
        </Section>

        <Section title={<>Proposed facts {proposed.length > 0 && <Badge tone="warn">{proposed.length}</Badge>}</>}>
          <ProposedFacts facts={proposed} evidence={evidence} onReviewed={done} />
        </Section>

        <Section title="Offices">
          {studio.offices.length === 0 ? (
            <Empty>No office recorded.</Empty>
          ) : (
            <ul className="rows">
              {studio.offices.map((o) => (
                <li key={o.id}>
                  <Sq kind={o.is_hq ? 'filled' : 'outline'} /> {o.city ?? <Unknown>city unknown</Unknown>}, {o.country ?? '?'}{' '}
                  <span className="muted small">
                    · {o.is_hq ? 'HQ' : 'other office'} · {o.lat !== null ? o.geocode_note ?? 'geocoded' : 'not geocoded — list only, not on the map'}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="muted small">Map positions are city centroids. {studio.geo_attribution}.</p>
        </Section>

        <Section title="Social profiles">
          {studio.socials.length === 0 ? (
            <Empty>No social profile recorded.</Empty>
          ) : (
            <ul className="rows">
              {studio.socials.map((s) => (
                <li key={s.id}>
                  <strong>{s.platform}</strong>{' '}
                  <a href={s.url} target="_blank" rel="noreferrer noopener">
                    {s.handle ?? s.url}
                  </a>{' '}
                  <span className="muted small">· {accessLabel(s.access_status)}</span>
                </li>
              ))}
            </ul>
          )}
          <SocialForm studioId={studio.id} onAdded={done} />
        </Section>
      </div>

      <aside className="studio-aside">
        <Section title="Tags">
          <TagEditor studio={studio} onChanged={done} />
        </Section>

        <Section title="Deep research">
          {studio.research_depth === 'deep' && studio.deep_settings ? (
            <>
              <KV
                rows={[
                  ['Since', <Time iso={studio.deep_since} />],
                  ['Cadence', `every ${studio.deep_settings.cadence_days} days`],
                  ['Lookback', `${studio.deep_settings.lookback_days} days`],
                  ['Max pages', String(studio.deep_settings.max_pages)],
                  [
                    'Channels',
                    <ul className="plain">
                      {studio.deep_settings.channels.map((c) => (
                        <li key={c}>
                          {c}{' '}
                          <span className="muted small">
                            · {channelLabel(studio.deep_settings?.channel_status?.[c])}
                          </span>
                        </li>
                      ))}
                    </ul>,
                  ],
                ]}
              />
            </>
          ) : (
            <p className="small muted">
              Light: profile from what was entered or found once on the website. Promote to Deep to collect on a schedule.
              {studio.deep_settings?.ended_at && (
                <>
                  {' '}
                  Was Deep until <Time iso={studio.deep_settings.ended_at} />; its history is kept.
                </>
              )}
            </p>
          )}
          {studio.schedules.length > 0 && (
            <ul className="rows small">
              {studio.schedules.map((s) => (
                <li key={s.id}>
                  <StatusMark status={s.status} /> {jobType(s.task_type)} ({s.channel}) · every {s.interval_days} d
                  {s.status !== 'ended' ? (
                    <>
                      {' '}
                      · next <Time iso={s.next_run_at} />
                    </>
                  ) : (
                    s.ended_reason && <span className="muted"> · {s.ended_reason}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Domains and aliases">
          {studio.domains.length === 0 ? (
            <Empty>No domain recorded.</Empty>
          ) : (
            <ul className="rows small">
              {studio.domains.map((d) => (
                <li key={d.domain}>
                  {d.domain} {d.is_primary && <span className="muted">· primary</span>}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {studio.merged_from.length > 0 && (
          <Section title="Merged into this record">
            <ul className="rows small">
              {studio.merged_from.map((m) => (
                <li key={m.studio_id}>
                  {m.name ?? 'unnamed'} {m.domain && <span className="muted">· {m.domain}</span>} · <Time iso={m.at} />
                  <div className="muted">
                    moved: {Object.entries(m.moved || {}).map(([k, n]) => `${n} ${k}`).join(', ') || 'nothing'}
                  </div>
                </li>
              ))}
            </ul>
          </Section>
        )}

        <Section title="Recent jobs">
          {studio.recent_jobs.length === 0 ? (
            <Empty>No job has run for this studio.</Empty>
          ) : (
            <ul className="rows small">
              {studio.recent_jobs.slice(0, 6).map((j) => (
                <li key={j.id}>
                  <StatusMark status={j.status} /> <Link to={`/jobs/${j.id}`}>{jobType(j.type)}</Link> · <Time iso={j.finished_at ?? j.created_at} />
                </li>
              ))}
            </ul>
          )}
        </Section>
      </aside>
    </div>
  );
}
